import * as M from '@radix-ui/react-dropdown-menu'
import { useEffect, useState, type ComponentProps, type ReactNode } from 'react'
import s from './Menu.module.css'

/**
 * A dropdown menu — the kit's first, and the shape the topbar's identity needed.
 *
 * Radix, like Select/Dialog/Tabs/Tooltip already in this kit, rather than a
 * hand-rolled popover: a menu is one of the few widgets where the accessible
 * behaviour is genuinely hard — roving focus with arrow keys, type-ahead, Escape
 * and outside-click closing, focus returning to the trigger, and `aria-expanded`
 * staying truthful. Every one of those is a way to ship something that looks
 * right and cannot be operated without a mouse, which is exactly what the a11y
 * gate exists to catch.
 *
 * `asChild` on the trigger so the caller supplies its own button and keeps its
 * own styling; this component owns the panel, not the thing that opens it.
 */
export function Menu({ trigger, children, align = 'end', testId, contentTestId, onCloseAutoFocus,
  collisionPadding = 8, anchor, open, defaultOpen, onOpenChange }: {
  /** The control that opens the menu. Trigger ownership is the DEFAULT — a menu
   *  that owns its trigger keeps `aria-expanded`, `aria-haspopup` and focus
   *  return truthful without the host doing anything about any of them. */
  trigger?: ReactNode
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  testId?: string
  /** The panel's own test id (#2375) — `testId` names the trigger. */
  contentTestId?: string
  onCloseAutoFocus?: ComponentProps<typeof M.Content>['onCloseAutoFocus']
  /**
   * What else occupies the viewport, so Radix flips the panel instead of
   * opening it under something.
   *
   * Radix's collision boundary is the VIEWPORT, and a FIXED overlay is inside
   * it — so a menu opening near the bottom of the window lands behind the
   * shell mode bar rather than flipping away from it. A host that renders one
   * says how much of the bottom is spoken for; the default 8 is unchanged for
   * every other call site.
   */
  collisionPadding?: ComponentProps<typeof M.Content>['collisionPadding']
  /**
   * AN EXTERNALLY ANCHORED SURFACE, for the one case that genuinely has no
   * trigger to own (#2406 section 3): `DataGrid`'s cell and batch menus, whose
   * API is `(row, anchor, close, commit) => ReactNode`. The grid owns the cell
   * that was clicked and hands the host an element; the host supplies only the
   * menu. Passing `trigger` there would mean rendering a second, invisible
   * control beside a real one.
   *
   * Radix positions `Content` against its `Trigger`, and `DropdownMenu`
   * publishes no `Anchor` primitive — so in this mode the trigger is a
   * zero-opacity, pointer-transparent box over the anchor's rect, re-measured
   * on scroll and resize while the menu is open. Everything else — arrow keys,
   * type-ahead, Escape, outside click, focus return — is Radix's, exactly as in
   * the default mode. That is the point of not hand-rolling a second surface.
   */
  anchor?: HTMLElement | null
  /** Controlled open state. Required with `anchor`, since there is no trigger
   *  for Radix to toggle from. */
  open?: boolean
  /** Opens on mount and is then uncontrolled — for a gallery, a design capture
   *  or a host restoring state (#2406 kit-defects-b). */
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const [rect, setRect] = useState<DOMRect | null>(() => anchor?.getBoundingClientRect() ?? null)
  useEffect(() => {
    if (!anchor || open === false) return
    const measure = () => setRect(anchor.getBoundingClientRect())
    measure()
    /* `capture: true` — a menu anchored to a cell inside a scrolling grid moves
       with the grid's OWN scroll container, and a listener on `window` alone
       never hears that. The defect it prevents is a menu that stays put while
       the row it describes scrolls away underneath it. */
    addEventListener('scroll', measure, true)
    addEventListener('resize', measure)
    return () => { removeEventListener('scroll', measure, true); removeEventListener('resize', measure) }
  }, [anchor, open])

  const anchored = anchor != null
  return (
    <M.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={anchored ? false : undefined}>
      {anchored
        ? (
          <M.Trigger asChild data-testid={testId}>
            <span aria-hidden="true" tabIndex={-1} style={{
              position: 'fixed', pointerEvents: 'none', opacity: 0,
              left: rect?.left ?? 0, top: rect?.top ?? 0,
              width: rect?.width ?? 0, height: rect?.height ?? 0,
            }} />
          </M.Trigger>
        )
        : <M.Trigger asChild data-testid={testId}>{trigger}</M.Trigger>}
      <M.Portal>
        <M.Content className={s.content} align={align} sideOffset={6} collisionPadding={collisionPadding}
          data-testid={contentTestId}
          onCloseAutoFocus={onCloseAutoFocus}>
          {children}
        </M.Content>
      </M.Portal>
    </M.Root>
  )
}

/** A non-interactive heading — who this menu is about. */
Menu.Label = function MenuLabel({ children, testId }: { children: ReactNode; testId?: string }) {
  return <M.Label className={s.label} data-testid={testId}>{children}</M.Label>
}

Menu.Separator = function MenuSeparator() {
  return <M.Separator className={s.sep} />
}

/**
 * `asChild` is offered because most items here are LINKS, and a menu item that
 * is a real anchor can be opened in a new tab, copied, and read as a link by a
 * screen reader. Wrapping a link in a div with an onClick loses all three.
 */
Menu.Item = function MenuItem({ children, onSelect, asChild, danger, disabled, testId }: {
  children: ReactNode
  onSelect?: () => void
  asChild?: boolean
  danger?: boolean
  disabled?: boolean
  testId?: string
}) {
  return (
    <M.Item
      className={`${s.item}${danger ? ` ${s.danger}` : ''}`}
      asChild={asChild}
      data-testid={testId}
      disabled={disabled}
      onSelect={onSelect ? () => onSelect() : undefined}
    >
      {children}
    </M.Item>
  )
}

/**
 * A CHECKED item — a state, not a command (#2406 section 3).
 *
 * The console drew this as a glyph in a 14px column, nine times, inside plain
 * items. A screen reader was told those were ordinary menu items and the state
 * was a character it read as content or skipped entirely. `CheckboxItem`
 * carries `role="menuitemcheckbox"` and `aria-checked`, so the state is in the
 * accessibility tree rather than in the font.
 *
 * The menu stays OPEN on a tick, which is right for a column-visibility list:
 * closing after each one would make setting three columns a three-trip job.
 */
Menu.CheckItem = function MenuCheckItem({ children, checked, onSelect, disabled, testId }: {
  children: ReactNode
  checked: boolean
  onSelect?: (next: boolean) => void
  disabled?: boolean
  testId?: string
}) {
  return (
    <M.CheckboxItem className={s.item} checked={checked} disabled={disabled} data-testid={testId}
      onSelect={(e) => e.preventDefault()}
      onCheckedChange={(next) => onSelect?.(next === true)}>
      {/* `aria-hidden`: the tick is DECORATION. `aria-checked` already carries
          the state, and without this the glyph joins the item's accessible
          name — so a checked "All opportunities" is announced, and located, as
          "✓All opportunities". A page object matching `^All opportunities`
          then silently stops finding the option once it is the open one, which
          is the narrowest possible window for a locator to fail in. */}
      <span className={s.indicator} aria-hidden="true">
        <M.ItemIndicator>✓</M.ItemIndicator>
      </span>
      {children}
    </M.CheckboxItem>
  )
}

/**
 * ONE OF a set — the shape a view picker or a sort direction needs.
 *
 * Separate from `CheckItem` because the semantics differ where it counts: a
 * radio group announces "3 of 7" and moves a selection; a checkbox announces a
 * state per item. Drawing both as a tick and calling them the same thing is how
 * the console ended up with one `.chk` class doing two different jobs.
 */
Menu.RadioGroup = function MenuRadioGroup({ value, onValueChange, children }: {
  value: string
  onValueChange: (value: string) => void
  children: ReactNode
}) {
  return <M.RadioGroup value={value} onValueChange={onValueChange}>{children}</M.RadioGroup>
}

Menu.RadioItem = function MenuRadioItem({ value, children, disabled, testId }: {
  value: string
  children: ReactNode
  disabled?: boolean
  testId?: string
}) {
  return (
    <M.RadioItem className={s.item} value={value} disabled={disabled} data-testid={testId}>
      {/* `aria-hidden`: the tick is DECORATION. `aria-checked` already carries
          the state, and without this the glyph joins the item's accessible
          name — so a checked "All opportunities" is announced, and located, as
          "✓All opportunities". A page object matching `^All opportunities`
          then silently stops finding the option once it is the open one, which
          is the narrowest possible window for a locator to fail in. */}
      <span className={s.indicator} aria-hidden="true">
        <M.ItemIndicator>✓</M.ItemIndicator>
      </span>
      {children}
    </M.RadioItem>
  )
}
